import React, { useRef, useState } from "react";
import { useDispatch } from "react-redux";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import AddDashboardSubSectionDialog from "pages/web-pages/AddDashboardSubSectionDialog";
import {
  updateDashboardSection,
  deleteDashboardSection,
} from "actions/webPages";
import {
  DashboardSectionContainer,
  Section,
  SectionHeader,
  SectionBody,
  AddSubSection,
  SubSectionWrapper,
  StyledPaper,
} from "styles/pages/web-pages";

const getVisibleSections = (sections, isAdmin) => {
  if (isAdmin) return sections;

  return sections
    ?.map((section) => ({
      ...section,
      subSectionsData: section?.subSectionsData?.filter(
        (subSection) => subSection?.active !== false,
      ),
    }))
    ?.filter((section) => section?.subSectionsData?.length > 0);
};

const DashboardSection = ({ sections, refresh, isAdmin = true }) => {
  const dispatch = useDispatch();
  const [selectedSection, setSelectedSection] = useState(false);
  const [editSubSection, setEditSubSection] = useState(null);
  const [dragOverState, setDragOverState] = useState(null);
  const dragItemRef = useRef(null);
  const blockDragRef = useRef(false);

  const visibleSections = getVisibleSections(sections, isAdmin);

  const getSectionKey = (section) => section?._id || section?.sectionName;

  const updateSubSections = async (section, subSectionsData) => {
    await dispatch(
      updateDashboardSection({
        sectionName: section?.sectionName,
        subSectionsData,
      }),
    );
    refresh();
  };

  const handleDragStart = (e, section, index) => {
    if (blockDragRef.current) {
      e.preventDefault();
      return;
    }

    const sectionKey = getSectionKey(section);
    dragItemRef.current = { sectionKey, index };
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(index));
  };

  const handleDragOver = (e, section, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    const sectionKey = getSectionKey(section);
    setDragOverState((prev) => {
      if (prev?.sectionKey === sectionKey && prev?.index === index) {
        return prev;
      }
      return { sectionKey, index };
    });
  };

  const handleDragEnd = () => {
    dragItemRef.current = null;
    blockDragRef.current = false;
    setDragOverState(null);
  };

  const handleDrop = async (e, section, dropIndex) => {
    e.preventDefault();
    e.stopPropagation();

    const dragItem = dragItemRef.current;
    const dragIndex =
      dragItem?.index ?? Number(e.dataTransfer.getData("text/plain"));

    setDragOverState(null);
    dragItemRef.current = null;
    blockDragRef.current = false;

    if (
      Number.isNaN(dragIndex) ||
      dragIndex === dropIndex ||
      dragItem?.sectionKey !== getSectionKey(section)
    ) {
      return;
    }

    const subSectionsData = [...(section?.subSectionsData || [])];
    const [movedItem] = subSectionsData.splice(dragIndex, 1);
    subSectionsData.splice(dropIndex, 0, movedItem);

    await updateSubSections(section, subSectionsData);
  };

  const handleActionMouseDown = (e) => {
    if (!e.target.closest(".drag-handle")) {
      blockDragRef.current = true;
    }
  };

  const unblockDragStart = () => {
    blockDragRef.current = false;
  };

  const toggleActive = async (e, section, index) => {
    e.stopPropagation();
    const subSectionsData = [...(section?.subSectionsData || [])];
    const current = subSectionsData[index];
    subSectionsData[index] = {
      ...current,
      active: current?.active === false,
    };
    await updateSubSections(section, subSectionsData);
  };

  return visibleSections?.length ? (
    <>
      <DashboardSectionContainer>
        {visibleSections.map((section) => (
          <StyledPaper key={section._id} $isAdmin={isAdmin}>
            <Section $isAdmin={isAdmin}>
              <SectionHeader $isAdmin={isAdmin}>
                <h2>{section?.sectionName}</h2>
                {isAdmin && (
                  <DeleteIcon
                    onClick={async () => {
                      await dispatch(
                        deleteDashboardSection({
                          sectionName: section?.sectionName,
                        }),
                      );
                      refresh();
                    }}
                  />
                )}
              </SectionHeader>
              <SectionBody $isAdmin={isAdmin}>
                {section?.subSectionsData?.map((subSection, i) => {
                  const sectionKey = getSectionKey(section);
                  const isActive = subSection?.active !== false;
                  const isDragOver =
                    dragOverState?.sectionKey === sectionKey &&
                    dragOverState?.index === i;

                  return (
                    <SubSectionWrapper
                      key={
                        subSection?.subSectionLink ||
                        `${sectionKey}-subsection-${i}`
                      }
                      className={`${isDragOver ? "drag-over" : ""} ${
                        !isActive ? "inactive" : ""
                      }`}
                      $isAdmin={isAdmin}
                      draggable={isAdmin}
                      onDragStart={(e) => handleDragStart(e, section, i)}
                      onDragOver={(e) => handleDragOver(e, section, i)}
                      onDragEnter={(e) => handleDragOver(e, section, i)}
                      onDragEnd={handleDragEnd}
                      onDrop={(e) => handleDrop(e, section, i)}
                      onClick={() => {
                        if (!isAdmin) {
                          window.open(subSection?.subSectionLink);
                        }
                      }}
                    >
                      <Paper elevation={8} draggable={false}>
                        {isAdmin && (
                          <div
                            className="card-actions"
                            onMouseDown={handleActionMouseDown}
                            onMouseUp={unblockDragStart}
                            onMouseLeave={unblockDragStart}
                          >
                            <div className="card-actions-left">
                              <DragIndicatorIcon className="drag-handle" />
                              <EditIcon
                                className="edit-icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditSubSection({
                                    ...subSection,
                                    index: i,
                                  });
                                  setSelectedSection(section);
                                }}
                              />
                            </div>
                            <div className="card-actions-right">
                              <Tooltip
                                title={
                                  isActive ? "Hide on site" : "Show on site"
                                }
                                arrow
                              >
                                <span>
                                  {isActive ? (
                                    <VisibilityIcon
                                      className="visibility-icon"
                                      onClick={(e) =>
                                        toggleActive(e, section, i)
                                      }
                                    />
                                  ) : (
                                    <VisibilityOffIcon
                                      className="visibility-icon inactive"
                                      onClick={(e) =>
                                        toggleActive(e, section, i)
                                      }
                                    />
                                  )}
                                </span>
                              </Tooltip>
                              <DeleteIcon
                                className="delete-icon"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  const payload = {
                                    sectionName: section?.sectionName,
                                    subSectionsData:
                                      section?.subSectionsData?.filter(
                                        (_, j) => i !== j,
                                      ),
                                  };
                                  await dispatch(
                                    updateDashboardSection(payload),
                                  );
                                  refresh();
                                }}
                              />
                            </div>
                          </div>
                        )}
                        {subSection?.subSectionImage &&
                          (isAdmin ? (
                            <div
                              className="img"
                              style={{
                                backgroundImage: `url(${subSection?.subSectionImage})`,
                              }}
                            />
                          ) : (
                            <div className="img-wrapper">
                              <img
                                src={subSection?.subSectionImage}
                                alt={subSection?.subSectionName || ""}
                              />
                            </div>
                          ))}
                        <p
                          className="card-title"
                          title={subSection?.subSectionName}
                        >
                          {subSection?.subSectionName}
                        </p>
                      </Paper>
                    </SubSectionWrapper>
                  );
                })}
                {isAdmin && (
                  <AddSubSection onClick={() => setSelectedSection(section)}>
                    <AddIcon />
                  </AddSubSection>
                )}
              </SectionBody>
            </Section>
          </StyledPaper>
        ))}
      </DashboardSectionContainer>
      {!!selectedSection && (
        <AddDashboardSubSectionDialog
          data={editSubSection}
          open={!!selectedSection}
          setOpen={() => {
            setSelectedSection(false);
            setEditSubSection(null);
          }}
          addSubSection={async (data) => {
            const subSectionsData = [
              ...(selectedSection?.subSectionsData || []),
            ];

            if (data?.index >= 0) {
              const index = data.index;
              const { index: _, ...subSectionData } = data;
              subSectionsData[index] = subSectionData;
            } else {
              subSectionsData.push({ ...data, active: true });
            }

            const payload = {
              sectionName: selectedSection?.sectionName,
              subSectionsData,
            };
            await dispatch(updateDashboardSection(payload));
            setSelectedSection(false);
            setEditSubSection(null);
            refresh();
          }}
        />
      )}
    </>
  ) : null;
};

export default DashboardSection;
